import { FC, useState } from 'react';
import styles from './Login.module.css';
import loginBg from '../../assets/images/loginBackground2.png';
import synovahLogo from '../../assets/images/logo.svg';
import { userManager } from '../../utils/auth.js';
import DashboardIllustration from '../illustration/DashboardIllustration.tsx';

const Login: FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await userManager.clearStaleState();

      const user = await userManager.getUser();
      console.log("While Login, user is:", user);

      if (user) {
        console.log("User already logged in:", user);
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 3000);
        return;
      }

      await userManager.signinRedirect();
    } catch (err) {
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className={styles.loginContainer}>
      {/* Animated Background */}
      <div className={styles.backgroundWrapper}>
        <div className={styles.hexagonPattern} style={{ backgroundImage: `url(${loginBg})` }} />
        <div className={styles.gradientOverlay} />
        <div className={styles.particlesLayer}>
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className={styles.particle}
              style={{
                '--delay': `${i * 0.3}s`,
                '--x': `${Math.random() * 100}%`,
                '--y': `${Math.random() * 100}%`,
                '--duration': `${15 + Math.random() * 10}s`
              } as React.CSSProperties}
            />
          ))}
        </div>
        {/* Decorative circles */}
        <div className={styles.decorativeCircle} style={{ '--size': '300px', '--x': '10%', '--y': '20%' } as React.CSSProperties} />
        <div className={styles.decorativeCircle} style={{ '--size': '200px', '--x': '85%', '--y': '70%' } as React.CSSProperties} />
      </div>

      {/* Main Content */}
      <div className={styles.contentWrapper}>
        {/* Logo and Branding */}
        <div className={styles.brandSection}>
          <div className={styles.logoContainer}>
            <img 
              src={synovahLogo} 
              alt="Synovah HealthTech" 
              className={styles.brandLogo}
            />
          </div>
          <p className={styles.tagline}>Innovating and Digitalizing Metadata for Clinical Excellence</p>
        </div>

        {/* Medical Illustration */}
        <div className={styles.illustrationContainer}>
          <div className={styles.illustrationGlow} />
         <DashboardIllustration className={styles.medicalIllustration} />
        </div>

        {/* Login Button */}
        <div className={styles.loginSection}>
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className={styles.loginButton}
          >
            {isLoading ? (
              <div className={styles.loaderContainer}>
                <div className={styles.loader} />
                <span>Authenticating...</span>
              </div>
            ) : (
              <>
                <span className={styles.buttonText}>Secure Login</span>
                <svg className={styles.buttonIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </>
            )}
          </button>
          <p className={styles.securityNote}>
            <svg className={styles.lockIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Protected by enterprise-grade security
          </p>
        </div>

        {/* Copyright */}
        <div className={styles.copyright}>
          <div className={styles.copyrightDivider} />
          <p>&copy; 2025 SYNOVAH HEALTHTECH. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;